import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../../common/Table/CustomTable/CustomTable';
import classes from './MainTable.module.scss';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../../utils/constants';
import { columns } from './helper';
import { addNewClaim, updateClaim } from '../../../../../actions/expense';
import { ExpenseColumnType } from './types';
import ExpenseAttachment from '../ExpenseAttachment/ExpenseAttachment';
import { tneAxiosClient } from '../../../../../api/apiSettings';
import BaseModal from '../../../../common/ModalWindows/BaseModal';
// eslint-disable-next-line import/no-cycle
import ExpenseCardList from '../ExpenseCardList/ExpenseCardList';
// eslint-disable-next-line import/no-cycle
import { EditExpenseContext } from '../../Expense';
import { basicErrorHandler, numberWithCommas } from '../../../../../utils/helpers';

export default function MainTable({
  expenseCurrency,
  totalClaim,
  data,
  save,
  submit,
  deleteLine,
  approve,
  reject,
  saving,
  isApprover
}) {
  const size = useWindowSize();
  const { t } = useTranslation();
  const expensetypes = useSelector((state) => state.constFromApi.expensetypes);
  const currencies = useSelector((state) => state.constFromApi.currencies);
  const dispatch = useDispatch();
  const [showAttachment, setShowAttachment] = useState({ show: false, id: '', expense: {}, checked: false });
  const [savingFiles, setSavingFiles] = useState(false);
  const expenseDetails = data?.ExpenseDetails;
  const ctx = useContext(EditExpenseContext);

  const addHandler = () => {
    dispatch(addNewClaim());
    ctx.setHasChanges(true);
  };

  const changeHandler = async (type, id, newValue) => {
    let expense = expenseDetails.find((claim) => claim.Id === id);

    switch (type) {
      case ExpenseColumnType.DATE:
        expense = {
          ...expense,
          Date: newValue
        };
        break;
      case ExpenseColumnType.CATEGORY:
        expense = {
          ...expense,
          CategoryId: newValue,
          Category: {
            Name: expensetypes?.find((expensetype) => expensetype?.value === newValue)?.label || 'Travel'
          }
        };
        break;
      case ExpenseColumnType.EXPENSE_AMOUNT:
        expense = {
          ...expense,
          ExpenseAmount: newValue || 0,
          TotalAmount: expense.FxAmount > 0 ? newValue * expense.FxAmount || 0 : newValue || 0
        };
        break;
      case ExpenseColumnType.FX_CURRENCY:
        expense = {
          ...expense,
          FxCurrencyId: newValue.key,
          FxCurrency: {
            CurrencyName: newValue.label
          },
          FxAmount: 1,
          TotalAmount: 1 * expense.ExpenseAmount
        };
        break;
      case ExpenseColumnType.RATE:
        expense = {
          ...expense,
          FxAmount: newValue || 0,
          TotalAmount: (newValue || 0) * expense.ExpenseAmount
        };
        break;
      case ExpenseColumnType.COMMENT:
        expense = {
          ...expense,
          Comment: newValue
        };
        break;
      case ExpenseColumnType.RECEIPT:
        if (!newValue) {
          setShowAttachment({ show: !showAttachment.show, id, expense });
        }
        expense = {
          ...expense,
          NoReceiptCheck: newValue
        };
        dispatch(updateClaim(expense));
        if (newValue) {
          ctx.setHasChanges(true);
        } else {
          ctx.setHasChanges(false);
        }
        return;
      case ExpenseColumnType.RECEIPT_DELETE:
        expense = {
          ...expense,
          attachments: expense?.attachments?.filter((file) => file.Id !== newValue),
          ExpenseAttachments: expense?.ExpenseAttachments?.filter((file) => file.Id !== newValue)
        };
        setShowAttachment({ show: true, id, expense, checked: newValue?.checked });

        break;
      case ExpenseColumnType.RECEIPT_SAVE: {
        const formData = new FormData();
        expense = {
          ...expense,
          attachments: !newValue.checked ? newValue.files : [],
          ExpenseAttachments: !newValue.checked ? [...expense.ExpenseAttachments, ...newValue.files] : [],
          NoReceiptCheck: newValue.checked
        };
        if (expense?.Type === 'New') {
          setShowAttachment({ show: false, id: '', expense, checked: newValue?.checked });
          ctx.setHasChanges(true);
          dispatch(updateClaim(expense));
          return;
        }
        newValue?.files?.map((file) => {
          formData.append('attachments', file);
          return formData;
        });

        if (!newValue.checked && newValue?.files?.length) {
          setSavingFiles(true);
          try {
            const result = await tneAxiosClient.post(`v1/expense/attachment/${id}`, formData);
            expense = {
              ...expense,
              ExpenseAttachments: result?.data?.Data?.Data
            };
          } catch (e) {
            basicErrorHandler(dispatch, e);
          }
          setSavingFiles(false);
        }
        setShowAttachment({ show: false, id: '', expense, checked: newValue.checked });
        expense = {
          ...expense,
          attachments: []
        };
        dispatch(updateClaim(expense));
        ctx.setHasChanges(true);
        return;
      }
      default:
        break;
    }

    dispatch(updateClaim(expense));
    ctx.setHasChanges(true);
  };

  const handleCloseAttachment = () => {
    setShowAttachment({ show: false, id: '' });
  };

  return (
    <div className={classes.maintable}>
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={data?.ExpenseDetails || []}
          columns={() =>
            columns(
              t,
              expensetypes,
              addHandler,
              changeHandler,
              deleteLine,
              ctx.isEdit,
              currencies,
              `${data.User.FirstName} ${data.User.LastName}`
            )
          }
          withFooter
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          footerMargin="0"
          bodyWithBorderRadius={false}
          withEmbedded
          bodyStyles={{ padding: '20px 26px 60px' }}
          embedContent={`${expenseCurrency || ''} ${numberWithCommas(parseFloat(totalClaim || 0).toFixed(2)) || 0} `}
        />
      ) : (
        <ExpenseCardList
          data={data}
          addHandler={addHandler}
          changeHandler={changeHandler}
          saveHandler={save}
          submitHandler={submit}
          totalClaim={totalClaim}
          expenseCurrency={expenseCurrency}
          deleteLineHandler={deleteLine}
          approveHandler={approve}
          rejectHandler={reject}
          saving={saving}
          editable={
            ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
              data?.Status?.Status
            ) && !isApprover
          }
        />
      )}

      <BaseModal
        open={showAttachment.show}
        fullscreen={false}
        closeHandler={() => setShowAttachment(false)}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        minWidth="550px"
      >
        <ExpenseAttachment
          data={showAttachment.expense}
          handleCloseAttachment={handleCloseAttachment}
          open={showAttachment.show}
          saveAttachment={(checked, files) =>
            changeHandler(ExpenseColumnType.RECEIPT_SAVE, showAttachment.id, { files, checked })
          }
          isEdit={ctx.isEdit}
          deleteAttachment={(id) => changeHandler(ExpenseColumnType.RECEIPT_DELETE, showAttachment.id, id)}
          savingFiles={savingFiles}
        />
      </BaseModal>
    </div>
  );
}
